import { CheckOutlined } from "@mui/icons-material";
import CalendarViewDayIcon from "@mui/icons-material/CalendarViewDay";
import { Alert, AlertTitle, FormControl, FormHelperText, Paper, Snackbar } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import DialogContentText from "@mui/material/DialogContentText";
import Grid from '@mui/material/Grid2';
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import * as yup from "yup";
import EditIcon from "../../../assets/images/icons/buttons/editIcon";
import SaveIcon from "../../../assets/images/icons/buttons/saveIcon";
import InfoIcon from "../../../assets/images/icons/sideMenu/infoIcon";
import ConfirmDialog from "../../../components/app/dialogs/confirmDialog/confirmDialog";
import ContentLoader from "../../../components/general/contentLoader/contentLoader";
import { CatalogueLanguages, Permissions } from "../../../helpers/constants";
import { eventStatus, formatDate } from "../../../helpers/date";
import { checkCatalogDisabled } from "../../../helpers/helpers";
import { dialogueContextTextDefaults } from "../../../helpers/theme/defaultProps";
import { useCountries } from "../../../hooks/countries/useCountries";
import { useExhibition } from "../../../hooks/exhibitions/useExhibition";
import useExhibitionAction, { UpdateExhibitor } from "../../../hooks/exhibitions/useExhibitionAction";
import useUserData from "../../../hooks/user/useUserData";
import { EXHIBITOR_PROFILE } from "../../../locales/components/namespaces";
import { useHasPermission } from "../../../modules/permission/hooks/useHasPermission";
import type { Exhibition, Exhibitor } from "../../../types/exhibition/types";
import CatalogDataEntry from "./catalogDataEntry";
import CatalogHeader from "./catalogHeader";
import CatalogLogo from "./catalogLogo";
import CatalogSocial from "./catalogSocial";
import CatalogBrands from "./catalogueBrands";
import { CatalogueInfo } from "./catalogueInfo";
import CatalogueWordPreview from "./catalogueWordPreview";

type Props = {
	exhibitor: Exhibitor, exhibitionId: string,
}

type InnerProps = Props & { exhibition: Exhibition }

const Section = ({ disabled, children }) => {
	return (
		<Paper
			sx={{ p: '32px', mb: 1, border: '1px solid #E8EBEC', borderRadius: '12px' }}
			variant={"outlined"}
		>
			{children}
		</Paper>
	)
}

export const showNoValueIfDisabled = (value, disabled) => disabled ? (!value || value === "") ? '' : value : value

const CatalogInner = (props: InnerProps) => {
	const { t } = useTranslation(EXHIBITOR_PROFILE)
	const { dateFormat } = useUserData()
	const {
		exhibitor,
		exhibitionId,
		exhibition,
		phoneCodesEl,
		phoneCodesEn,
		countriesEl,
		countriesEn,
		handleShowCatalogueInfo,
		loading
	} = props
	const {
		updateExhibitorProfile,
		exhibitorCatalogueConfirmReset,
		exhibitorCatalogueConfirm,
		setExhibitorEditMode
	} = useExhibitionAction()
	const isOrganizer = useHasPermission([Permissions.BeOrganizer], { shouldView: true })
	const isExhibitor = useHasPermission([Permissions.BeExhibitor], { shouldView: true })
	const [openConfirm, setOpenConfirm] = useState(false)
	const onSubmit = (values) => {
		updateExhibitorProfile(exhibitor.id, exhibitionId, values, UpdateExhibitor.Catalog)
		setOpenConfirm(false)
	}
	
	const editMode = useMemo(() => (exhibitor?.editMode), [exhibitor?.editMode]);
	const validationSchema = yup.object({
		'brand': yup.object({
			'el': exhibition.catalogueLanguage === CatalogueLanguages.EN ? yup.string() : yup.string().required(t('required')),
			'en': exhibition.catalogueLanguage === CatalogueLanguages.EN || exhibition.catalogueLanguage === CatalogueLanguages.ALL ? yup.string().matches(/^([A-Za-z^!@#$%&*()_+\-\\|{}[\].,<>?'":;/`=\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s\w]*)$/gi, t('Only latin and special characters allowed.')).required(t('required')) : yup.string()
		}),
		'social': yup.object({
			'email': yup.string().required(t('required')),
		}),
		'address1': yup.object({
			'country': yup.object({
				'en': yup.mixed().test(
					'required', t('required'), function (value) {
						return exhibition.hideCatalogueAddress ? true : exhibition.catalogueLanguage !== CatalogueLanguages.EL ? value && value?.id : true
					}
				),
				'el': yup.mixed().test(
					'required', t('required'), function (value) {
						return exhibition.hideCatalogueAddress ? true : exhibition.catalogueLanguage !== CatalogueLanguages.EN ? value && value?.id : true
					}
				),
			}),
			'phone': yup.object({
				'en': yup.mixed().test(
					'required', t('required'), function (value) {
						return exhibition.hideCatalogueAddress ? true : exhibition.catalogueLanguage !== CatalogueLanguages.EL ? value : true
					}
				),
				'el': yup.mixed().test(
					'required', t('required'), function (value) {
						return exhibition.hideCatalogueAddress ? true : exhibition.catalogueLanguage !== CatalogueLanguages.EN ? value : true
					}
				),
			}),
		}),
		brands0: yup.string().test(
			'not same as company name',
			t('The Brand must not be the same as the Company Name.'),
			function (value) {
				return (value !== this.parent['brand']?.en && value !== this.parent['brand']?.el) || !value
			}),
		brands1: yup.string().test(
			'not same as company name',
			t('The Brand must not be the same as the Company Name.'),
			function (value) {
				return (value !== this.parent['brand']?.en && value !== this.parent['brand']?.el) || !value
			}),
		brands2: yup.string().test(
			'not same as company name',
			t('The Brand must not be the same as the Company Name.'),
			function (value) {
				return (value !== this.parent['brand']?.en && value !== this.parent['brand']?.el) || !value
			}),
		brands3: yup.string().test(
			'not same as company name',
			t('The Brand must not be the same as the Company Name.'),
			function (value) {
				return (value !== this.parent['brand']?.en && value !== this.parent['brand']?.el) || !value
			}),
		brands4: yup.string().test(
			'not same as company name',
			t('The Brand must not be the same as the Company Name.'),
			function (value) {
				return (value !== this.parent['brand']?.en && value !== this.parent['brand']?.el) || !value
			}),
		brands5: yup.string().test(
			'not same as company name',
			t('The Brand must not be the same as the Company Name.'),
			function (value) {
				return (value !== this.parent['brand']?.en && value !== this.parent['brand']?.el) || !value
			}),
		brands6: yup.string().test(
			'not same as company name',
			t('The Brand must not be the same as the Company Name.'),
			function (value) {
				return (value !== this.parent['brand']?.en && value !== this.parent['brand']?.el) || !value
			}),
		brands7: yup.string().test(
			'not same as company name',
			t('The Brand must not be the same as the Company Name.'),
			function (value) {
				return (value !== this.parent['brand']?.en && value !== this.parent['brand']?.el) || !value
			}),
		brands8: yup.string().test(
			'not same as company name',
			t('The Brand must not be the same as the Company Name.'),
			function (value) {
				return (value !== this.parent['brand']?.en && value !== this.parent['brand']?.el) || !value
			}),
		brands9: yup.string().test(
			'not same as company name',
			t('The Brand must not be the same as the Company Name.'),
			function (value) {
				return (value !== this.parent['brand']?.en && value !== this.parent['brand']?.el) || !value
			})
	})
	
	const formik = useFormik({
		initialValues: {
			brand: {
				en: exhibitor.catalog.brand.en,
				el: exhibitor.catalog.brand.el,
			},
			profile: {
				en: exhibitor.catalog.profile.en,
				el: exhibitor.catalog.profile.el,
			},
			address1: {
				...exhibitor.address1,
				country: {
					el: typeof exhibitor.address1.country.el === 'string' && exhibitor.address1.country.el ? countriesEl.find(f => f.id === exhibitor.address1.country.el) : '',
					en: typeof exhibitor.address1.country.en === 'string' && exhibitor.address1.country.en ? countriesEn.find(f => f.id === exhibitor.address1.country.en) : '',
				},
				phoneCode: {
					el: typeof exhibitor.address1.phoneCode.el === 'string' && exhibitor.address1.phoneCode.el ? phoneCodesEl.find(f => f.id === exhibitor.address1.phoneCode.el) : '',
					en: typeof exhibitor.address1.phoneCode.en === 'string' && exhibitor.address1.phoneCode.en ? phoneCodesEn.find(f => f.id === exhibitor.address1.phoneCode.en) : '',
				}
			},
			address2: {
				...exhibitor.address2,
				country: {
					el: typeof exhibitor.address2.country.el === 'string' && exhibitor.address2.country.el ? countriesEl.find(f => f.id === exhibitor.address2.country.el) : '',
					en: typeof exhibitor.address2.country.en === 'string' && exhibitor.address2.country.en ? countriesEn.find(f => f.id === exhibitor.address2.country.en) : '',
				},
				phoneCode: {
					el: typeof exhibitor.address2.phoneCode.el === 'string' && exhibitor.address2.phoneCode.el ? phoneCodesEl.find(f => f.id === exhibitor.address2.phoneCode.el) : '',
					en: typeof exhibitor.address2.phoneCode.en === 'string' && exhibitor.address2.phoneCode.en ? phoneCodesEn.find(f => f.id === exhibitor.address2.phoneCode.en) : ''
				}
			},
			social: {
				website: exhibitor.website,
				email: exhibitor.emailCatalogue,
				fb: exhibitor.social.fb,
				instagram: exhibitor.social.instagram,
				linkedIn: exhibitor.social.linkedIn
			},
			brands0: exhibitor.brands[0] ? exhibitor.brands[0] : '',
			brands1: exhibitor.brands[1] ? exhibitor.brands[1] : '',
			brands2: exhibitor.brands[2] ? exhibitor.brands[2] : '',
			brands3: exhibitor.brands[3] ? exhibitor.brands[3] : '',
			brands4: exhibitor.brands[4] ? exhibitor.brands[4] : '',
			brands5: exhibitor.brands[5] ? exhibitor.brands[5] : '',
			brands6: exhibitor.brands[6] ? exhibitor.brands[6] : '',
			brands7: exhibitor.brands[7] ? exhibitor.brands[7] : '',
			brands8: exhibitor.brands[8] ? exhibitor.brands[8] : '',
			brands9: exhibitor.brands[9] ? exhibitor.brands[9] : '',
			kioskSign: exhibitor.kioskSign || ''
		},
		validationSchema: validationSchema,
		onSubmit: onSubmit,
	})
	const {
		catalogLocked,
		daysForCatalogSubmission,
		dateFrom,
		dateTo,
		hideCatalogueProfile,
		hideCatalogueAddress
	} = exhibition
	const {
		catalogDisabled,
		status,
		dayBeforeCatalogSubmission,
		canFillCatalog
	} = checkCatalogDisabled({isOrganizer, openExhibitorCatalogue: exhibitor.openExhibitorCatalogue, catalogLocked, daysForCatalogSubmission, dateFrom, dateTo})
	
	const inputVariant = 'filled'
	
	const confirmSubmit = (evt) => {
		formik.validateForm()
		.then((response) => {
				if (Object.keys(response).length === 0) {
					setOpenConfirm(true)
				} else {
					enqueueSnackbar(t('Please fill all required fields to continue!'), {
						variant: 'warning',
					})
				}
			}
		)
	}
	
	const confirmCatalogueData = (evt) => {
		formik.validateForm()
		.then((response) => {
				if (Object.keys(response).length === 0) {
					if (isExhibitor) {
						exhibitorCatalogueConfirm(exhibitionId, exhibitor.id)
					} else {
						enqueueSnackbar(t('Catalogue confirmation can only be done from exhibitors!'), {
							variant: 'warning',
						})
					}
				} else {
					enqueueSnackbar(t('Please fill all required fields to continue!'), {
						variant: 'warning',
					})
				}
			}
		)
	}
	const kioskSignDisabled = useMemo(() => (catalogDisabled || !editMode || !exhibition.isKioskSignEditable || exhibitor.isKioskSignReadOnly), [catalogDisabled, editMode, exhibition.isKioskSignEditable, exhibitor.isKioskSignReadOnly])

	return (
		<FormikProvider value={formik}>
			<Box component="form">
				<Box>
					{!catalogLocked ? (
						<React.Fragment>
							{status !== eventStatus.status.EXPIRED && !isNaN(daysForCatalogSubmission) && daysForCatalogSubmission > 0 && (
								<React.Fragment>
									{canFillCatalog ? (
										<Alert
											severity="info"
											sx={{
												borderRadius: '12px',
												background: '#E0F2FE',
												borderLeft: '3px solid #008AD2',
												color: '#5E666B',
												fontSize: '16px',
												lineHeight: '24px',
												mb: 2
											}}
										>
											<AlertTitle>{t('Info')}</AlertTitle>
											<Trans
												i18nKey={'catalog_submit_warning'}
												ns={EXHIBITOR_PROFILE}
												values={{
													date: formatDate(dayBeforeCatalogSubmission, dateFormat)
												}}
												defaults={"You can submit your catalogue information up until <bold>{{date}}</bold>."}
												components={{ bold: <strong/> }}
											/>
										</Alert>
									) : (
										<Alert
											severity="warning"
											sx={{
												borderRadius: '12px',
												background: '#FFFBEA',
												borderLeft: '3px solid #FC9D04',
												color: '#5E666B',
												fontSize: '16px',
												lineHeight: '24px',
												mb: 2
											}}
										>
											<AlertTitle>{t('Warning')}</AlertTitle>
											<Trans
												i18nKey={'catalog_submit_ended_warning'}
												ns={EXHIBITOR_PROFILE}
												values={{
													email: "info@rota.gr"
												}}
												defaults={"The period in which you can edit your catalogue information has ended."}
												components={{ bold: <strong/>, a: <a/> }}
											/>
										</Alert>
									)}
								</React.Fragment>
							)}
						</React.Fragment>
					) : (
						<Alert
							severity={"info"}
							sx={{
								borderRadius: '12px',
								background: '#E0F2FE',
								borderLeft: '3px solid #008AD2',
								color: '#5E666B',
								fontSize: '16px',
								lineHeight: '24px',
								mb: 2
							}}
						>
							<AlertTitle>{t('Info')}</AlertTitle>
							<Trans
								i18nKey={'catalog_submit_disabled'}
								ns={EXHIBITOR_PROFILE}
								values={{
									email: "support@rota.gr",
									date: formatDate(dayBeforeCatalogSubmission, formatDate.formatTypes.DATE_ONLY)
								}}
								defaults={"Catalogue editing is disabled for this event. You can only view your catalogue information. <br/> <strong>ΑΤΤΕΝΤΙΟΝ:</strong> Changes in catalogue are possible after contacting us. <br/> Please contact us at <a href='mailto:{{email}}'>{{email}}</a> if you need help."}
								components={{ bold: <strong/>, a: <a/> }}
							/>
						</Alert>
					)}
					<Grid container spacing={4}>
						<Grid size={{ xs: 12, sm: 12, md: 6 }} alignItems={'end'}>
							<Card
								sx={{
									p: '24px 32px',
									maxWidth: { xs: '100%', sm: '430px', md: '100%', lg: '475px' },
									border: '1px solid',
									borderColor: 'text.main',
									borderRadius: '12px',
									boxShadow: 'none'
								}}
							>
								<CatalogHeader
									exhibition={exhibition}
									exhibitor={exhibitor}
									disabled={catalogDisabled || !editMode}
									formik={formik}
									inputVariant={inputVariant}
								/>
							</Card>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 6 }} display={'flex'} alignItems={'end'}>
							{!catalogDisabled && (
								<Stack direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }} spacing={1}
									   width={'100%'} justifyContent={'end'} gap={1}>
									<Button
										variant={"contained"}
										size={"small"}
										startIcon={<InfoIcon/>}
										color="primary"
										onClick={(evt) => handleShowCatalogueInfo(true)}
										style={{ minWidth: 'unset' }}
									>
										{t("Instructions")}
									</Button>
									{editMode && (
										<Button
											size={"small"}
											onClick={confirmSubmit}
											startIcon={<SaveIcon/>}
											loading={loading}
											loadingPosition="start"
											variant="contained"
											color={"success"}
											style={{ minWidth: 'unset' }}
										>
											{t('Save')}
										</Button>
									)}
									{!editMode && (
										<>
											<Button
												size={"small"}
												startIcon={<EditIcon/>}
												variant="contained"
												color={"highlight"}
												onClick={() => {
													setExhibitorEditMode(exhibitor.id, true)
												}}
												style={{ minWidth: 'unset' }}
												disabled={catalogDisabled}
											>
												{t("Edit")}
											</Button>
											<Button
												variant={"contained"}
												size={"small"}
												startIcon={<CheckOutlined/>}
												color={'info'}
												onClick={(evt) => {
													confirmCatalogueData()
												}}
											>
												{t("Confirm Catalogue Data")}
											</Button>
										</>
									)}
								</Stack>
							)}
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Grid container spacing={2}>
								{(exhibition.catalogueLanguage === CatalogueLanguages.EL || exhibition.catalogueLanguage === CatalogueLanguages.ALL) && (
									<Grid size={{
										xs: 12,
										sm: 12,
										md: 12,
										lg: exhibition.catalogueLanguage !== CatalogueLanguages.ALL ? 12 : 6
									}}>
										<Section disabled={catalogDisabled || !editMode}>
											<CatalogDataEntry
												catalogueProfileLength={exhibitor.catalogueProfileLength ? exhibitor.catalogueProfileLength : exhibition.catalogueProfileLength}
												catalogueLanguage={exhibition.catalogueLanguage}
												disabled={catalogDisabled || !editMode}
												inputVariant={inputVariant}
												language={'el'}
												formik={formik}
												countriesEl={countriesEl}
												countriesEn={countriesEn}
												phoneCodesEl={phoneCodesEl}
												phoneCodesEn={phoneCodesEn}
												hideCatalogueProfile={hideCatalogueProfile}
												hideCatalogueAddress={hideCatalogueAddress}
											/>
										</Section>
									</Grid>
								)}
								{(exhibition.catalogueLanguage === CatalogueLanguages.EN || exhibition.catalogueLanguage === CatalogueLanguages.ALL) && (
									<Grid size={{
										xs: 12,
										sm: 12,
										md: 12,
										lg: exhibition.catalogueLanguage !== CatalogueLanguages.ALL ? 12 : 6
									}}
									>
										<Section disabled={catalogDisabled || !editMode}>
											<CatalogDataEntry
												catalogueProfileLength={exhibitor.catalogueProfileLength ? exhibitor.catalogueProfileLength : exhibition.catalogueProfileLength}
												catalogueLanguage={exhibition.catalogueLanguage}
												disabled={catalogDisabled || !editMode}
												inputVariant={inputVariant}
												language={'en'}
												formik={formik}
												countriesEl={countriesEl}
												countriesEn={countriesEn}
												phoneCodesEl={phoneCodesEl}
												phoneCodesEn={phoneCodesEn}
												hideCatalogueProfile={hideCatalogueProfile}
												hideCatalogueAddress={hideCatalogueAddress}
											/>
										</Section>
									</Grid>
								)}
							</Grid>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Section disabled={catalogDisabled || !editMode}>
								<CatalogSocial
									disabled={catalogDisabled || !editMode}
									inputVariant={inputVariant}
									formik={formik}
								/>
							</Section>
						</Grid>
						{
							exhibition.catalogEnableKioskSign && (
								<Grid size={12}>
									<Section disabled={kioskSignDisabled || !editMode}>
										<Box sx={{ display: 'flex', alignItems: 'center' }}>
											<CalendarViewDayIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }}/>
											<FormControl
												variant="standard"
												color={'error'}
												fullWidth
												error={
													formik.touched['kioskSign'] &&
													Boolean(formik.errors['kioskSign'])
												}
											>
												<TextField
													slotProps={{
														inputLabel: {
															shrink: true,
														},
														htmlInput: {
															maxLength: 250
														},
													}}
													disabled={kioskSignDisabled}
													fullWidth
													size={"small"}
													variant={inputVariant}
													label={t('Kiosk Sign')}
													name={'kioskSign'}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													value={formik.values.kioskSign}
												/>
												<FormHelperText sx={{ color: '#d32f2f' }}>
													<ErrorMessage name={'kioskSign'}/>
												</FormHelperText>
											</FormControl>
										</Box>
									</Section>
								</Grid>
							)
						}
						{exhibition.hasExhibitorBrands && (
							<Grid size={12}>
								<Section disabled={catalogDisabled || !editMode}>
									<CatalogBrands
										disabled={catalogDisabled || !editMode}
										inputVariant={inputVariant}
										formik={formik}
									/>
								</Section>
							</Grid>
						)}
					</Grid>
				</Box>
			</Box>
			<ConfirmDialog
				hasBtnIcons={false}
				onYes={formik.submitForm}
				onNo={() => setOpenConfirm(false)}
				handleDialog={() => setOpenConfirm(false)}
				title={t('Confirm Catalogue Submission')}
				open={Boolean(openConfirm)}
				yesBtnText={t('YES')}
				noBtnText={t('NO')}
				maxWidth={'md'}
			>
				<DialogContentText
					{...dialogueContextTextDefaults}
				>
					{t('You may have empty optional fields in your catalogue.')}
					<br/><br/>
					{t('Do you still wish to submit your catalogue?')}
					<br/><br/>
				</DialogContentText>
				<CatalogueWordPreview values={formik.values} exhibitor={exhibitor} exhibition={exhibition}
									  hideCatalogueProfile={hideCatalogueProfile}
									  hideCatalogueAddress={hideCatalogueAddress}/>
			</ConfirmDialog>
		</FormikProvider>
	)
}

const Catalog = (props: Props) => {
	const { t } = useTranslation(EXHIBITOR_PROFILE)
	const { exhibition, loading } = useExhibition()
	const [openSuccess, setOpenSuccess] = useState(false)
	const [showCatalogueInfo, setShowCatalogueInfo] = useState(true)
	const handleShowCatalogueInfo = (show) => setShowCatalogueInfo(show)
	const {
		loading: countriesLoading,
		countriesEl,
		countriesEn,
		phoneCodesEn,
		phoneCodesEl
	} = useCountries()
	const handleCloseAlert = () => setOpenSuccess(false)
	return (
		<React.Fragment>
			{exhibition && !countriesLoading ? (
				<>
					<CatalogInner
						{...props}
						exhibition={exhibition}
						countriesEl={countriesEl}
						countriesEn={countriesEn}
						phoneCodesEl={phoneCodesEl}
						phoneCodesEn={phoneCodesEn}
						handleShowCatalogueInfo={handleShowCatalogueInfo}
					/>
					<CatalogueInfo
						{...props}
						open={showCatalogueInfo}
						exhibition={exhibition}
						onClose={() => setShowCatalogueInfo(false)}
					/>
				</>
			) : (
				<ContentLoader
					loading={loading}
				/>
			)}
			<Snackbar
				open={openSuccess}
				anchorOrigin={{
					vertical: 'top', horizontal: 'right',
				}}
				autoHideDuration={6000}
				onClose={handleCloseAlert}>
				<Alert
					onClose={handleCloseAlert}
					severity="success"
					sx={{ width: '100%' }}
				>
					{t('Saved successfully!')}
				</Alert>
			</Snackbar>
		</React.Fragment>
	)
}

export default Catalog